import type { ReactElement, Ref } from "react";
import { useTranslation } from "react-i18next";
import {
  formatLocalYmd,
  type FigurePreference,
  type SessionResult,
  type WorkoutEnergyReadout,
} from "@heraion/shared";
import {
  OffscreenCapture,
  ShareCardRenderer,
  resolveTheme,
  useShareCardData,
  useShareCardHandle,
  type PostSessionSummaryHandle,
  type WeightUnit,
} from "@heraion/ui";

import { useThemePalette } from "../theme";
import { useShellProfile } from "../ThemeProfileBridge";
import { notifyViaWindow } from "../confirm";
import { shareOrDownloadPng } from "./shareCardCaptureWeb";

// The web share of one session's card: the off-screen 9:16 renderer and the
// share step behind it. Shared by the post-save card (PostSessionSummaryCard)
// and the Workouts detail pane (#318), so the web wires it once.
export function useWebShareCard(
  handleRef: Ref<PostSessionSummaryHandle> | undefined,
  input: {
    result: SessionResult;
    energy: WorkoutEnergyReadout | null;
    locale: string;
    unit: WeightUnit;
    resolveName: (slug: string, fallback?: string) => string;
  },
): { sharing: boolean; onShare: () => Promise<void>; capture: ReactElement } {
  const { result, energy, locale, unit, resolveName } = input;
  const { t } = useTranslation();
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { doc } = useShellProfile();
  const figure: FigurePreference =
    (doc?.["preferredFigure"] as FigurePreference | undefined) ?? "masculine";
  const socialHandle = (doc?.["socialHandle"] as string | undefined) ?? null;

  // Share-card data shaping (packages/ui, shared with mobile's own mount:
  // date formatting, i18n label mapping, sessionResultToShareCardData()).
  const { shareData } = useShareCardData({
    result,
    socialHandle,
    unit,
    locale,
    energy,
    resolveName,
    heatmapRamp: theme.heatmapRamp,
  });

  const { cardRef, sharing, onShare } = useShareCardHandle(
    handleRef ?? null,
    async (uri) => {
      const dateSlug = formatLocalYmd(new Date(result.computedAt));
      await shareOrDownloadPng(
        uri,
        `metria-${dateSlug}.png`,
        t("mobile.postSession.shareTitle", { defaultValue: "$t(brand.product) session" }),
      );
    },
    (msg) =>
      notifyViaWindow(
        t("mobile.postSession.shareErrorTitle", { defaultValue: "Share failed" }),
        msg,
      ),
  );

  const capture = (
    <OffscreenCapture>
      <ShareCardRenderer
        ref={cardRef}
        theme={theme}
        isDark={dark}
        data={shareData}
        figure={figure}
      />
    </OffscreenCapture>
  );

  return { sharing, onShare, capture };
}
